/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { daysFromNow } from '@zapier/date';
import { Alert, Link } from '@zapier/design-system';
import { Price } from '@zapier/currency';

import type { Result } from '../useAlertBanner/alertBannerNotificationsQuery';
import { buildUpgradeUrl, getShouldRenderSunsetCopy } from './utils';
import { BannerProps } from './types';
import { Fragment } from 'react';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';

const isExcludedUrl = () =>
  typeof window !== 'undefined' &&
  window.location.pathname.includes('team-account-setup');

export const shouldRender = (data: Result['data']) => {
  const { isTrialing, trialEndDate } = data.currentAccount;
  return !!isTrialing && !!trialEndDate && !isExcludedUrl();
};

export const Component = function ProfessionalTrialing(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const { data, currency } = props;
  const { currentAccount, nextPlan } = data;
  const { trialEndDate } = currentAccount;
  const trialDaysRemaining = daysFromNow(trialEndDate!);
  const daysText = trialDaysRemaining === 1 ? 'day' : 'days';
  // @ref ul-1: Divide by 12 since `nextPlan` should always be annual.
  const nextPlanAmount = (nextPlan?.amount || 0) / 12;
  const upgradeUrl = buildUpgradeUrl({
    currentPlanName: currentAccount.plan.name,
    upgradePlanName: data.nextPlan?.name,
    interval: currentAccount.plan.interval,
  });

  const onClickLink = () => {
    props.trackEvent('Trial: clicked banner');
  };

  const shouldRenderCopy = getShouldRenderSunsetCopy(
    currentAccount.campaignOffers
  );
  const offer = shouldRenderCopy && currentAccount.campaignOffers[0];

  return (
    <Alert
      layout={props.layout}
      title="Welcome to your Professional trial!"
      type="info"
    >
      <p>
        {`You have ${trialDaysRemaining} ${daysText} to try Zapier’s `}
        <Link
          href={getFullyQualifiedURL(
            '/help/account/billing-pricing/understand-your-zapier-premium-trial'
          )}
          onClick={onClickLink}
        >
          paid features
        </Link>
        .{' '}
        {offer ? (
          <Fragment>
            <Link href={getFullyQualifiedURL('/app/pricing')}>Upgrade</Link>{' '}
            before November 14th, 2022 and get a free year of Zapier.
          </Fragment>
        ) : (
          <Fragment>
            <Link href={upgradeUrl} onClick={onClickLink}>
              Upgrade
            </Link>{' '}
            anytime
            {nextPlanAmount > 0 && (
              <span>
                {' '}
                for as low as{' '}
                <Price currency={currency}>{nextPlanAmount}</Price>
                /month.
              </span>
            )}
          </Fragment>
        )}
      </p>
    </Alert>
  );
};
